import cx from "classnames";
import React, {
	type PropsWithChildren,
	useMemo,
	useEffect,
	useCallback,
} from "react";
import { useNationalDogSurveyContext } from "../../NationalDogSurvey/NationalDogSurvey.context";
import { NDSTooltip } from "../../components/NDSTooltip";
import { SectionFuzzyBorderBottom } from "../../context/PageManager/PageManager.Borders";
import { Colours } from "../../utils/colours";
import * as styles from "./NDSTemplateQuestion.module.scss";

interface NDSTemplateQuestionProps extends PropsWithChildren {
	// The title
	title: string;
	subTitle?: string;
	// Whether the question is hidden
	hidden?: boolean;
	screenTooltip?: string;
	className?: string;
}
export const NDSTemplateQuestion: React.FC<NDSTemplateQuestionProps> = ({
	title,
	subTitle,
	children,
	hidden,
	className,
	screenTooltip,
}) => {
	const { screenState, surveyState, currentDogName } =
		useNationalDogSurveyContext();
	const formRef = React.useRef<HTMLDivElement>(null);

	const tooltipId = useMemo(
		() => `tooltip-${Math.floor(Math.random() * 1000)}`,
		[],
	);

	const getTitle = useCallback(() => {
		return title
			.replace("[dogNum]", screenState.dogCount === 1 ? "dog" : "dogs")
			.replace("[dogName]", currentDogName.split("*!*")[0] || "your dog")
			.replace("[firstName]", surveyState.warmData?.firstName || "");
	}, [title, screenState.dogCount, currentDogName, surveyState.warmData]);

	useEffect(() => {
		if (!hidden) {
			// This is an SPA so we need to change the page title when the question is shown
			document.title = getTitle();
		}
	}, [hidden, getTitle]);

	if (hidden) return null;

	return (
		<section
			className={cx(
				styles.ndsQuestionTemplateContainer,
				hidden && styles.visuallyHidden,
			)}
		>
			<header className={styles.ndsQuestionTemplateHeader}>
				<h1 className={styles.ndsQuestionTemplateHeaderTitle}>{getTitle()}</h1>
				<p className={styles.ndsQuestionTemplateHeaderSubtitle}>{subTitle}</p>
			</header>
			<SectionFuzzyBorderBottom
				borderColour={Colours.WHITE}
				nextSection={{
					primaryColour: Colours.SUNSHINE_YELLOW,
					sectionId: "test-0",
					sectionType: "intro",
				}}
			/>

			<div
				ref={formRef}
				className={cx(styles.ndsQuestionTemplateWrapper, className)}
			>
				{children}
				{screenTooltip ? (
					<div className={styles.tooltipContainer}>
						<NDSTooltip id={tooltipId} text={screenTooltip} />
					</div>
				) : null}
			</div>
		</section>
	);
};
